import React, { forwardRef, useImperativeHandle } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { Paper } from '@material-ui/core'
import { IntegrationType } from '../../../server/mpsklad_core/Entity/IntegrationType'
import { TFormValues } from '../productForm/productForm.types'
import { ImageDropzoneList } from '../productForm/ui/imageDropzoneList'
import { IntegrationSelector } from '../productForm/ui/integrationSelector'
import { AttributeLoadingProvider } from '../productForm/lib/attributeLoadingContext'
import { MainFields } from '../productForm/ui/mainFields'
import { MarketplacesFields } from '../productForm/ui/marketplacesFields'
import { ProductFormItemRef } from './productFormItem.types'
import { ProductCardModel } from '../../../server/mpsklad_core/Models/MpCard/ProductCardModel'
import { Button } from '../../shared/ui/button'
import { normalizeDescriptionHtml } from '../../shared/lib/htmlDescriptionUtility'

type Props = {
  data: ProductCardModel
  handleButton?: () => void
  buttonText?: string
  isSubmitting?: boolean
  enableReadonlyMarkets?: boolean
}

export const ProductFormItem = forwardRef<ProductFormItemRef, Props>(({ data, handleButton, isSubmitting, enableReadonlyMarkets, buttonText }, ref) => {
  const descriptionAttr = data.attributes.find(a => a.name === 'Описание')

  const methods = useForm<TFormValues>({
    defaultValues: {
      attributes: data.attributes.filter(a => a.name && a.name !== 'Описание' && !a.integrationType),
      description: descriptionAttr
                 ? {...descriptionAttr, currentValue: normalizeDescriptionHtml(descriptionAttr.currentValue)}
                 : null,
      ozon: {
        include: !!data.ozonCategory?.id,
        category: data.ozonCategory,
        attributes: data.attributes.filter(a => a.integrationType === IntegrationType.Ozon)
      },
      wildberries: {
        include: !!data.wbCategory?.id,
        category: data.wbCategory,
        attributes: data.attributes.filter(a => a.integrationType === IntegrationType.Wildberries)
      },
      yandexMarket: {
        include: !!data.yandexMarketCategory?.id,
        category: data.yandexMarketCategory,
        attributes: data.attributes.filter(a => a.integrationType === IntegrationType.YandexMarket)
      },
      moySklad: {
        include: !!data.moySkladCategory?.id || !!data.moySkladProductId,
        category: data.moySkladCategory,
        attributes: data.attributes.filter(a => a.integrationType === IntegrationType.MoySklad)
      },
      images: data.images || []
    }
  })

  const readonlyMarkets: IntegrationType[] = enableReadonlyMarkets
    ? [
      ...(data.ozonCategory?.id || data.attributes.some(a => a.integrationType === IntegrationType.Ozon)
        ? [IntegrationType.Ozon]
        : []),
      ...(data.wbCategory?.id || data.attributes.some(a => a.integrationType === IntegrationType.Wildberries)
        ? [IntegrationType.Wildberries]
        : []),
      ...(data.yandexMarketCategory?.id || data.attributes.some(a => a.integrationType === IntegrationType.YandexMarket)
        ? [IntegrationType.YandexMarket]
        : []),
      ...(data.moySkladCategory?.id || data.moySkladProductId || data.attributes.some(a => a.integrationType === IntegrationType.MoySklad)
        ? [IntegrationType.MoySklad]
        : [])
    ]
    : []



  useImperativeHandle(ref, () => ({
    submit: async () => {
      const isValid = await methods.trigger()

      if (!isValid) {
        return { error: 'Validation failed' }
      }

      const values = methods.getValues()
      return { data: values }
    }
  }))

  return (
    <FormProvider {...methods}>
      <AttributeLoadingProvider>
        <Paper elevation={1} style={{padding: 16, marginBottom: 24}}>
          <ImageDropzoneList />
          <IntegrationSelector readOnlyMarkets={readonlyMarkets} />
          <MainFields/>
          <MarketplacesFields />
          {handleButton && (
              <Button onClick={handleButton} disabled={isSubmitting}>
                {buttonText}
              </Button>
          )}
        </Paper>
      </AttributeLoadingProvider>
    </FormProvider>
  )
})
